import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Home.css';
import { updatePageSEO, addStructuredData, seoData } from '../utils/SeoUtils';

function Home() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);

    // Update SEO for home page
    updatePageSEO(seoData.home.title, seoData.home.description, seoData.home.keywords);

    // Add enhanced structured data for homepage
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'MEGATON Serviços de Munck e Guindaste',
      image: 'https://megatonguindastes.com.br/images/logo/megaton-logo.png',
      '@id': 'https://megatonguindastes.com.br',
      url: 'https://megatonguindastes.com.br',
      telephone: '66999230430',
      priceRange: '$$',
      address: {
        '@type': 'PostalAddress',
        streetAddress: 'Av. Santos Dumont, 63 - Jardim Monte Libano',
        addressLocality: 'Rondonópolis',
        addressRegion: 'MT',
        postalCode: '78740-000',
        addressCountry: 'BR',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: -16.4708,
        longitude: -54.6396,
      },
      openingHours: ['Mo-Fr 07:00-17:00', 'Sa 07:00-11:00'],
      serviceArea: {
        '@type': 'GeoCircle',
        geoMidpoint: {
          '@type': 'GeoCoordinates',
          latitude: -16.4708,
          longitude: -54.6396,
        },
        geoRadius: '100000',
      },
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Serviços de Locação',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Locação de Munck',
              description: 'Locação de muncks para içamento e transporte',
            },
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: 'Locação de Guindaste',
              description: 'Locação de guindastes para construção e indústria',
            },
          },
        ],
      },
    };

    addStructuredData(structuredData);
  }, []);

  // Dynamically import all images from the cover folder
  const importAll = (r) => {
    const images = {};
    r.keys().map((item, index) => {
      return (images[item.replace('./', '')] = r(item));
    });
    return images;
  };

  // Natural sort function for proper numeric ordering
  const naturalSort = (a, b) => {
    return a.localeCompare(b, undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  };

  // Get all images from the cover folder
  const importedImages = importAll(
    require.context('/public/images/cover', false, /\.(png|jpe?g|svg)$/)
  );

  // Convert to array of filenames with proper numeric sorting
  const coverImages = Object.keys(importedImages)
    .sort(naturalSort)
    .map((img) => `/images/cover/${img}`);

  // Change image every 10 seconds
  useEffect(() => {
    if (coverImages.length === 0) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % coverImages.length);
    }, 10000); // 10 seconds

    return () => clearInterval(interval);
  }, [coverImages.length, currentImageIndex]); // Reset timer when currentImageIndex changes

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % coverImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + coverImages.length) % coverImages.length);
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && coverImages.length > 1) {
      nextImage();
    }
    if (isRightSwipe && coverImages.length > 1) {
      prevImage();
    }
  };

  return (
    <div className="home">
      <section className="hero">
        <div
          className="slideshow-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {coverImages.map((image, index) => (
            <div
              key={index}
              className={`slide ${index === currentImageIndex ? 'active' : ''}`}
              style={{
                backgroundImage: `url(${image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
          ))}

          {coverImages.length > 1 && (
            <>
              <button className="slideshow-arrow prev" onClick={prevImage}>
                &#8249;
              </button>
              <button className="slideshow-arrow next" onClick={nextImage}>
                &#8250;
              </button>
            </>
          )}

          <div className="hero-overlay">
            <div className="hero-content">
              <h1>Serviços Profissionais de Munck e Guindaste</h1>
              <p>Seu parceiro confiável em soluções de içamento e transporte pesado</p>
              <div className="hero-buttons">
                <Link to="/contato" className="cta-button primary">
                  Solicitar Orçamento
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="services">
        <div className="container">
          <h2>Nossos Serviços</h2>
          <div className="services-grid">
            <Link to="/munck" className="service-card">
              <h3>🚛 Locação de Munck</h3>
              <p>
                Serviços profissionais de munck para construção, mudanças e operações de içamento
                pesado.
              </p>
            </Link>
            <Link to="/guindaste" className="service-card">
              <h3>🏗️ Operações com Guindaste</h3>
              <p>
                Operadores especializados para operações complexas de içamento e projetos de
                construção.
              </p>
            </Link>
            <div className="service-card">
              <h3>⚡ Serviço de Emergência</h3>
              <p>Atendimento emergencial 24/7 para operações urgentes de içamento e resgate.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="why-choose-us">
        <div className="container">
          <h2>Por que Escolher a MEGATON?</h2>
          <div className="features-grid">
            <div className="feature">
              <h4>✅ Equipe Experiente</h4>
              <p>Anos de experiência em operações de içamento pesado</p>
            </div>
            <div className="feature">
              <h4>✅ Equipamentos Modernos</h4>
              <p>Máquinas bem conservadas e de última geração</p>
            </div>
            <div className="feature">
              <h4>✅ Segurança em Primeiro Lugar</h4>
              <p>Protocolos rigorosos de segurança e operadores certificados</p>
            </div>
            <div className="feature">
              <h4>✅ Preços Competitivos</h4>
              <p>Preços justos e transparentes para todos os serviços</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Pronto para Começar?</h2>
          <p>Entre em contato para um orçamento gratuito do seu projeto</p>
          <Link to="/contato" className="cta-button large">
            Fale Conosco Agora
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
